//
// useElmish: a React Hook to allow a reducer to issue "commands" which
// will be dispatched after it has completed a reduction.
//
// Similar to https://github.com/ncthbrt/react-use-elmish .
//
// `src/apps/properties/Store.js` serves as an example of using this hook.
//

import React from 'react'
import { useReducer as useReinspectReducer } from 'reinspect'

//
// Mutable module-level shared variable: serial number of the last command that has been run so far.
// Keeping track of this lets us avoid running any requested command more than once.
//
let lastSerialNumber = 0

//
// Takes a state and returns a new state which includes the given command
// queued up for execution.  To be used from inside a reducer.
//
export function enqueueCommand(state: any, command: any) {
  const serialNumber = state.nextSerialNumber || lastSerialNumber + 1
  const oldQueuedCommands = state.queuedCommands || []
  let newQueuedCommands = oldQueuedCommands.slice(0, 100)
  newQueuedCommands.unshift({ ...command, serialNumber: serialNumber })
  const newState = {
    ...state,
    queuedCommands: newQueuedCommands,
    nextSerialNumber: serialNumber + 1
  }
  return newState
}

//
// Helper function for useElmish hook.
//
// We cycle through all queued commands in the state that we haven't yet executed and
// we pass them to `runCommand`.
//
// After we execute a command, we set `lastSerialNumber` to its serial number.  Thus,
// when we see a command, if its serial number is less than or equal to `lastSerialNumber`,
// we know we have already run it, and should not run it again.
//
function processQueuedCommands(state: any, dispatch: any, runCommand: any) {
  if (!state.queuedCommands) {
    return
  }
  /* Loop backwards so that we enact the effects in the order they were queued */
  for (let i = state.queuedCommands.length - 1; i >= 0; i--) {
    let command = state.queuedCommands[i]
    if (command.serialNumber > lastSerialNumber) {
      runCommand(command, dispatch, state)
      lastSerialNumber = command.serialNumber
    }
  }
}

//
// Custom useElmish hook.
//
export function useElmish(reducer: any, initialState: any, runCommand: any) {
  const useReducerResult = React.useReducer(reducer, initialState)

  const [state, dispatch]: any = useReducerResult
  React.useEffect(() => {
    processQueuedCommands(state, dispatch, runCommand)
  }, [state.queuedCommands])
  return useReducerResult
}

//
// Version of useElmish that uses reinspect.
//
// Note that when using reinspect, it's important that reinspect gets a chance to
// respond to the internal action fired by its StateInspector component's useEffect
// hook, before your app dispatches any of its own actions.  A simple way to ensure
// this is to add a DelayedMountContainer component to the hierarchy, above the main
// app component but below the component that uses the useElmishWithReinspect hook.
//
export function useElmishWithReinspect(reducer: any, initialState: any, runCommand: any, reinspectLabel: any) {
  const useReducerResult = useReinspectReducer(reducer, initialState, (x) => x, reinspectLabel)

  const [state, dispatch]: any = useReducerResult
  React.useEffect(() => {
    processQueuedCommands(state, dispatch, runCommand)
  }, [state.queuedCommands])
  return useReducerResult
}
